import type { ConversationMessageContextView } from "@/api/autoparts";
import { ImageWithFallback } from "@/components/figma/ImageWithFallback";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Package, ShoppingBag, Store, X } from "@/components/icons";

type ConversationMessageContextProps = {
  context?: ConversationMessageContextView;
  compact?: boolean;
  onRemove?: () => void;
  removeLabel?: string;
};

export function ConversationMessageContext({
  context,
  compact = false,
  onRemove,
  removeLabel = "Remove attachment",
}: ConversationMessageContextProps) {
  if (!context) {
    return null;
  }

  const contextType = context.type.toLowerCase();
  const isMultipleOrders =
    contextType === "orders" ||
    /^\d+\s+orders?\s+attached:/i.test(context.label);
  const isOrder = contextType === "order" || isMultipleOrders;
  const isShop = contextType === "shop";
  const products = context.products ?? [];
  const isMultipleProducts = products.length > 1 || contextType === "products";
  const ContextIcon = isOrder ? ShoppingBag : isShop ? Store : Package;
  const contextBadge = isMultipleOrders
    ? "Orders"
    : isOrder
      ? "Order"
      : isShop
        ? "Shop"
        : isMultipleProducts
          ? `${products.length || 2} products`
          : "Product";
  const images = Array.from(
    new Set([
      ...context.images.filter(Boolean),
      ...products
        .map((product) => product.imageUrl?.trim() ?? "")
        .filter(Boolean),
    ]),
  );
  const visibleProducts = products.slice(0, compact ? 3 : 6);

  return (
    <div className="mb-3 overflow-hidden rounded-xl border border-current/15 bg-background/80 text-foreground">
      <div className="flex items-center justify-between gap-3 border-b border-current/10 px-3 py-2">
        <div className="flex min-w-0 items-center gap-2">
          <ContextIcon className="h-4 w-4 shrink-0 text-accent" />
          <p className="truncate text-xs font-semibold">{context.label}</p>
        </div>
        <div className="flex shrink-0 items-center gap-1.5">
          <Badge variant="outline" className="shrink-0 text-[10px]">
            {contextBadge}
          </Badge>
          {onRemove ? (
            <Button
              type="button"
              variant="ghost"
              size="icon-xs"
              className="text-muted-foreground hover:text-foreground"
              aria-label={removeLabel}
              title={removeLabel}
              onClick={onRemove}
            >
              <X className="h-3.5 w-3.5" />
            </Button>
          ) : null}
        </div>
      </div>

      {visibleProducts.length > 0 ? (
        <div className="space-y-1.5 p-2">
          {visibleProducts.map((product) => (
            <div
              key={product.id}
              className="flex items-center gap-2 rounded-lg border border-border bg-background p-2"
            >
              {product.imageUrl ? (
                <ImageWithFallback
                  src={product.imageUrl}
                  alt={product.name}
                  className={`${compact ? "h-10 w-10" : "h-12 w-12"} shrink-0 rounded-md border border-border object-cover`}
                />
              ) : (
                <div
                  className={`${compact ? "h-10 w-10" : "h-12 w-12"} flex shrink-0 items-center justify-center rounded-md border border-border bg-muted`}
                >
                  <Package className="h-4 w-4 text-muted-foreground" />
                </div>
              )}
              <p className="min-w-0 flex-1 truncate text-xs font-medium">
                {product.name}
              </p>
            </div>
          ))}
          {products.length > visibleProducts.length ? (
            <p className="px-1 text-[11px] text-muted-foreground">
              +{products.length - visibleProducts.length} more products
            </p>
          ) : null}
        </div>
      ) : images.length > 0 ? (
        <div
          className={`grid gap-2 p-2 ${
            images.length === 1 ? "grid-cols-1" : "grid-cols-2"
          }`}
        >
          {images.slice(0, compact ? 2 : 4).map((image, index) => (
            <ImageWithFallback
              key={`${context.id}-${image}-${index}`}
              src={image}
              alt={`${context.label} product ${index + 1}`}
              className={`${
                compact ? "h-16" : "h-24"
              } w-full rounded-lg border border-border object-cover`}
            />
          ))}
        </div>
      ) : null}
    </div>
  );
}
